<template>
  <FloatingElement
    v-if="visible"
    :x="x"
    :y="y"
    ref="popoverElement"
    transition-name="round-actions-popover__transition"
    :x-offset="-baseXOffset + offset"
    y-alignment="bottom"
    class="round-actions-popover"
    :style="[
      {
        '--pointerOffset': `${pointerOffset}px`
      }
    ]"
  >
    <button
      v-if="itemIndex !== -1"
      type="button"
      class="round-actions-popover__control round-actions-popover__prev"
      @click.stop.prevent="prev"
    >
      <lucide-icon name="chevron-left" />
    </button>
    <button
      v-if="nextPageAvailable"
      type="button"
      class="round-actions-popover__control round-actions-popover__next"
      @click.stop.prevent="next"
    >
      <lucide-icon name="chevron-right" />
    </button>

    <div
      class="round-actions-popover__content"
      :style="{ maxHeight: maxHeight ? `${maxHeight}px` : 'none' }"
    >
      <!-- OVERVIEW -->
      <div
        v-if="impulseSource == null"
        class="round-actions-popover__overview"
      >
        <div class="popup__label">
          {{ formattedRoundName }}
        </div>
        <template v-for="content of simulationContent" :key="content.id">
          <div
            v-if="
              safeSimulationIds.length > 1 &&
              content.impulseSources.length > 0
            "
            class="round-actions-popover__overviewsimulationtitle"
          >
            {{ content.simulation.title }}
          </div>
          <template v-if="content.incidents.length > 0">
            <div class="label">
              <store-item-icon
                type="incident"
                class="round-actions-popover__titleicon"
              />
              {{ $t("objectNames.incident", content.incidents.length) }}
            </div>
            <div class="list round-actions-popover__section">
              <button-item
                v-for="incident in content.incidents"
                :key="incident.id"
                class="round-actions-popover__entry"
                @click="inspect(content.id, incident, $event)"
              >
                <template #default>
                  <span>{{ incident.title }}</span>
                </template>
                <template #icons>
                  <button
                    type="button"
                    class="button-icon button-item__icon"
                    @click="showInfo($event, incident)"
                  >
                    <lucide-icon name="info" />
                  </button>
                </template>
              </button-item>
            </div>
          </template>

          <template v-if="content.actions.length > 0">
            <div class="label">
              <store-item-icon
                type="action"
                class="round-actions-popover__titleicon"
              />
              {{ $t("objectNames.action", content.actions.length) }}
            </div>
            <div class="list round-actions-popover__section">
              <button-item
                v-for="action in content.actions"
                :key="action.id"
                class="round-actions-popover__entry"
                @click="inspect(content.id, action, $event)"
              >
                <span>{{ action.title }}</span>
                <template #icons>
                  <button
                    type="button"
                    class="button-icon button-item__icon"
                    @click="showInfo($event, action)"
                  >
                    <lucide-icon name="info" />
                  </button>
                </template>
              </button-item>
            </div>
          </template>

          <template v-if="content.scheduledEffects.length > 0">
            <div class="label">
              <store-item-icon
                type="impulse"
                class="round-actions-popover__titleicon"
              />
              {{ $t("objectNames.impulse", content.scheduledEffects.length) }}
            </div>
            <div class="list round-actions-popover__section">
              <button-item
                v-for="impulse in content.scheduledEffects"
                :key="impulse.id"
                class="round-actions-popover__entry"
                @click="inspect(content.id, impulse, $event)"
              >
                <item
                  :id="impulse.affectedId"
                  v-slot="{ item }"
                  :type="impulse.affectedType"
                >
                  {{ item.title }}
                </item>
                <template #after-text>
                  <effect-amount
                    :effect="impulse"
                    :rounds="content.rounds"
                    :round-index="content.round?.index"
                    :index="0"
                    :targets="targets"
                    :resources="resources"
                  />
                </template>
              </button-item>
            </div>
          </template>
        </template>
      </div>

      <!-- INDIVIDUAL VIEW -->
      <template
        v-if="impulseSourceItem != null && selectedSimulation != null"
      >
        <div class="popup__label">
          {{ $t(`objectNames.${impulseSourceItem.type}`) }}<br />
        </div>

        <impulse-source-info
          :item="impulseSourceItem"
          :simulation-id="selectedSimulation.id"
          :round-index="roundIndex"
        >
          <template v-if="safeSimulationIds.length > 1" #simulation-title>
            <div class="round-actions-popover__simulationtitle">
              {{ selectedSimulation.title }}
            </div>
          </template>
        </impulse-source-info>
      </template>
    </div>
  </FloatingElement>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onBeforeUnmount } from "vue";
import { notNull } from "@/helpers/utility/Type";

import Item from "@editor/items/StoreItem.vue";
import ButtonItem from "@core/input/ButtonItem.vue";
import ImpulseSourceInfo from "@editor/items/impulse/ImpulseSourceInfo.vue";
import EffectAmount from "@editor/items/effect/EffectAmount.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
import StoreItemIcon from "../StoreItemIcon.vue";

import { useIncidentStore } from "../incident/incidents.store";
import { useSimulationStore } from "../simulation/simulations.store";
import { useActionStore } from "../action/actions.store";
import { useOverlay } from "@editor/ui/overlayDialog.store";
import { formatRoundName } from "./useRoundTime";
import { useTargetStore } from "../target/targets.store";
import { useResourceStore } from "../resource/resources.store";
import { commonOrDefault } from "@/helpers/utility/CollectionFunctions";

import type { Handleable } from "@core/types/Events";
import type { ScheduledEffectItem } from "../scheduledEffect/ScheduledEffectItem";
import type { ActionItem } from "../action/ActionItem";
import type { IncidentItem } from "../incident/IncidentItem";
import FloatingElement from "@core/ui/FloatingElement.vue";

const props = withDefaults(
  defineProps<{
    x: number;
    y: number;
    roundIndex: number;
    visible?: boolean;
    simulationIds?: number[];
    simulationId: number | null;
    maxHeight?: number;
    impulseSource?:
      | { type: "action"; id: number }
      | { type: "incident"; id: number }
      | { type: "scheduledEffect"; id: string }
      | null;
    hideInvisibles?: boolean;
  }>(),
  {
    visible: true,
    simulationIds: () => [],
    roundIndex: 0,
    impulseSource: null,
    hideInvisibles: false
  }
);

const emit = defineEmits(["close", "update:impulse-source", "update:simulation-id"]);

const offset = ref(0);
const baseXOffset = 154;

function closeActionsPopover(mouseEvent: MouseEvent) {
  const path = mouseEvent.composedPath();
  if (
    path.find((el) => el instanceof HTMLElement && (el as HTMLElement).classList.contains('round-actions-popover')) != null
  )
    return;
  emit("close");
}

/**
 * The simulation ids, including the selected simulation id.
 */
const safeSimulationIds = computed(() => {
  const simulationIds = props.simulationIds.slice();
  if (simulationIds.length == 0 && props.simulationId != null) {
    simulationIds.push(props.simulationId);
  }
  return simulationIds;
});
const selectedSimulation = computed(() => {
  if (props.simulationId == null) return;
  return useSimulationStore().items[props.simulationId];
});

const targets = computed(() => useTargetStore().items);
const resources = computed(() => useResourceStore().items);

const simulationContent = computed(() => {
  const simulationStore = useSimulationStore();
  const incidentStore = useIncidentStore();
  const actionStore = useActionStore();

  return safeSimulationIds.value.map((simulationId) => {
    const simulation = simulationStore.items[simulationId];

    const round = simulation.rounds[props.roundIndex];
    const roundName = formatRoundName(
      simulation.durationType,
      simulation.roundUnit,
      simulation.roundLength,
      props.roundIndex,
      simulation.timeStart
    );

    const activatedIncidents = round?.activatedIncidents ?? [];
    const incidents = incidentStore.getArrayByIds(
      activatedIncidents.map((r) => r.incidentId)
    ).filter(notNull).filter((incident) => props.hideInvisibles ? incident.visibility != "hidden" : true);

    const actionPlan = simulationStore.getActionPlan(
      simulation.id,
      props.roundIndex
    );
    const actions = actionStore.getArrayByIds(
      actionPlan.plannedActionIds
    );

    const scheduledEffects = actionPlan.scheduledEffects;

    const impulseSources = [...incidents, ...actions, ...scheduledEffects];

    return {
      id: simulation.id,
      simulation,
      rounds: simulation.rounds,
      round,
      roundName,
      incidents,
      actions,
      scheduledEffects,
      impulseSources
    };
  });
});
const impulseSourceList = computed(() => {
  return simulationContent.value.flatMap((c) => c.impulseSources);
});
const currentSimulationImpulseSources = computed(() => {
  if (props.simulationId == null) return [];
  const simIndex = safeSimulationIds.value.indexOf(props.simulationId);
  return simulationContent.value[simIndex].impulseSources || [];
});
const impulseSourceItem = computed(() => {
  if (props.simulationId == null) return null;
  if (props.impulseSource == null) return null;

  return currentSimulationImpulseSources.value.find(
    (item) =>
      item.type == props.impulseSource?.type &&
      item.id == props.impulseSource?.id
  );
});

const itemIndex = computed(() => {
  if (props.simulationId == null) return -1;
  if (props.impulseSource == null) return -1;

  return currentSimulationImpulseSources.value.findIndex(
    (page) =>
      page.type == props.impulseSource?.type &&
      page.id == props.impulseSource?.id
  );
});
const simulationIndex = computed(() => {
  if (props.simulationId == null) return -1;
  return safeSimulationIds.value.indexOf(props.simulationId);
});
const pointerOffset = computed(() => {
  return Math.max(Math.min(150, offset.value), -140);
});
const nextPageAvailable = computed(() => {
  if (impulseSourceList.value.length == 0) return false;
  if (props.simulationId == null) return true;
  if (props.impulseSource == null) return true;

  if (simulationIndex.value < safeSimulationIds.value.length - 1) return true;
  if (itemIndex.value < currentSimulationImpulseSources.value.length - 1)
    return true;

  return false;
});

const formattedRoundName = computed(() => {
  const durationType = commonOrDefault(
    simulationContent.value,
    (simcontent) => simcontent.simulation.durationType,
    "roundAmount"
  );
  const roundUnit = commonOrDefault(
    simulationContent.value,
    (simcontent) => simcontent.simulation.roundUnit,
    "round"
  );
  const roundLength = commonOrDefault(
    simulationContent.value,
    (simcontent) => simcontent.simulation.roundLength,
    1
  );
  const timeStart = commonOrDefault(
    simulationContent.value,
    (simcontent) => simcontent.simulation.timeStart,
    null
  );
  return formatRoundName(
    durationType,
    roundUnit,
    roundLength,
    props.roundIndex,
    timeStart
  );
});

watch(
  () => props.visible, 
  (open) => {
    if (open) {
      updatePosition();
      window.addEventListener("pointerdown", closeActionsPopover, true);
    } else {
      window.removeEventListener(
        "pointerdown",
        closeActionsPopover,
        true
      );
    }
  }
);
      
onMounted(() => {
  if (props.visible) {
    updatePosition();
    window.addEventListener("pointerdown", closeActionsPopover, true);
  }
});
onBeforeUnmount(() => {
  window.removeEventListener("pointerdown", closeActionsPopover, true);
});


function updatePosition() {
  const mainElement = document.querySelector(".main");
  if (!mainElement) return;

  const mainRect = mainElement.getBoundingClientRect();
  const offsetRect = {
    left: props.x - baseXOffset,
    right: props.x + baseXOffset
  };
  const BUTTON_SAFE_SPACE = 25;
  const offsetRight = Math.ceil(
    Math.ceil(offsetRect.right) - (mainRect.right - BUTTON_SAFE_SPACE)
  );
  const offsetLeft = Math.ceil(
    mainRect.left + BUTTON_SAFE_SPACE - Math.floor(offsetRect.left)
  );

  if (offsetRight > 0) {
    offset.value = -offsetRight;
  }
  if (offsetLeft > 0) {
    offset.value = offsetLeft;
  }
}

function next() {
  // first: try moving to the next item in the current simulation
  let nextIndex = itemIndex.value + 1;
  if (nextIndex < currentSimulationImpulseSources.value.length) {
    const item = currentSimulationImpulseSources.value[nextIndex];
    emit("update:impulse-source", item);
    return;
  }

  // move to the first item of the next simulation with items
  let nextSimulationIndex = simulationIndex.value + 1;
  while (nextSimulationIndex < safeSimulationIds.value.length) {
    const simulationId = props.simulationIds[nextSimulationIndex];
    const impulseSources =
      simulationContent.value[nextSimulationIndex].impulseSources;

    // jump to the next simulation if this simulation has no actions
    if (impulseSources.length == 0) {
      nextSimulationIndex++;
      continue;
    }

    const item = impulseSources[0];
    return inspect(simulationId, item);
  }

  inspect(null, null);
}

function prev() {
  // first: try moving to the previous item in the current simulation
  let prevIndex = itemIndex.value - 1;
  if (prevIndex >= 0) {
    const item = currentSimulationImpulseSources.value[prevIndex];
    inspect(props.simulationId, item);

    return;
  }

  // move to the last item of the previous simulation with items
  let prevSimulationIndex = simulationIndex.value - 1;
  while (prevSimulationIndex >= 0) {
    const simulationId = props.simulationIds[prevSimulationIndex];
    const impulseSources =
      simulationContent.value[prevSimulationIndex].impulseSources;

    // jump to the next simulation if this simulation has no actions
    if (impulseSources.length == 0) {
      prevSimulationIndex--;
      continue;
    }

    const item = impulseSources[impulseSources.length - 1];
    inspect(simulationId, item);
    return;
  }

  inspect(selectedSimulation.value?.id || null, null);
}

/**
 * show details of the effects in the current round
 */
function inspect(
  simulationId: number | null | undefined,
  item: ActionItem | IncidentItem | ScheduledEffectItem | null,
  mouseEvent?: MouseEvent & Handleable
) {
  if (mouseEvent && mouseEvent.hasBeenHandled) return;

  let switchedSimulation = simulationId != props.simulationId;
  if (switchedSimulation) {
    emit("update:simulation-id", simulationId);
  }

  // if the item is selected, deselect it.
  if (props.visible && props.impulseSource == item && !switchedSimulation) {
    emit("close");
  } else {
    emit("update:impulse-source", item);
  }
}

function showInfo(
  mouseEvent: MouseEvent & Handleable,
  item: null | { type: "action" | "incident"; id: number } = null
) {
  mouseEvent.hasBeenHandled = true;
  if (item == null) return;

  useOverlay().setItem(item);
}
</script>

<style lang="scss">
.round-actions-popover {
  width: 22rem;

  z-index: 1;
  font-size: 14px;
  text-align: left;

  background-color: var(--color-background-deep);
  border: 1px solid transparent;
  filter: drop-shadow(0 0 1px black)
    drop-shadow(1px 6px 8px var(--color-shadow-2));

  &__entry {
    min-width: 13rem;
  }

  &__content {
    display: flex;
    flex-direction: column;
    padding: 1em;
    padding-top: 0;
  }

  &__overview {
    overflow-x: hidden;
    overflow-y: auto;
  }

  &__control {
    background-color: var(--color-background-medium);
    color: var(--color-action);
    font-size: 1.6rem;
    text-align: center;
    padding: 0;
    position: absolute;
    border: none;
    height: 6rem;
    width: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    top: 50%;
    transform: translateY(-50%);
    &:focus {
      outline: none;
    }
    &:active {
      color: var(--color-action-contrast);
      background-color: var(--color-action);
    }
  }
  &__prev {
    right: 100%;
  }
  &__next {
    left: 100%;
  }

  &__titleicon {
    font-size: 1em;
    margin-right: 5px;
  }

  &__overviewsimulationtitle {
    font-weight: 600;
    // text-wrap: balance;
    color: var(--color-text-medium);
    padding: 0.5rem 0;
    width: 21rem;
    margin-bottom: 0.5rem;
    padding-block-start: 0.5rem;
    border-bottom: 1px solid var(--color-grey);
  }
  &__simulationtitle {
    font-weight: 600;
    // text-wrap: balance;
    margin-inline: -1rem;
    color: var(--color-text-medium);
    background-color: var(--color-background-medium);
    padding: 0.5rem 1.5rem;
    padding-block-start: 0.5rem;
    border-top: 1px solid var(--color-grey);
    border-bottom: 1px solid var(--color-grey);
  }

  &__section {
    padding: 0.5rem;
  }
  &__section:has(~ .roundactions__section) {
    padding-bottom: 1.25rem;
    border-bottom: 1px solid var(--color-grey);
    margin-bottom: 0.75rem;
  }

  &::after {
    content: " ";
    top: 100%;
    left: calc(50% - var(--pointerOffset));
    border: solid transparent;
    height: 0;
    width: 0;
    position: absolute;
    pointer-events: none;
    border-color: transparent;
    border-top-color: var(--color-background-deep);
    border-width: 11px;
    margin-left: -11px;
  }

  &__transition {
    &-enter-active {
      transition:
        opacity 200ms ease-out,
        transform 200ms cubic-bezier(0.215, 0.61, 0.355, 1);
    }
    &-leave-active {
      transition:
        opacity 100ms ease-out,
        transform 100ms ease-out;
    }

    &-enter-from {
      transform: scale3d(0.95, 0.95, 1);
      opacity: 0;
    }
    &-leave-to {
      transform: scale3d(1, 1, 1);
      opacity: 0;
    }
  }
}
</style>
